/**
 * `useProofs` — TanStack Query hooks for the per-user proof vault.
 *
 * Server is the source of truth (`/api/v1/proofs`, JWT-scoped). The
 * legacy [`proofsStore`](../stores/proofsStore.ts) Zustand slice
 * remains as an **offline cache** so the Vault page stays usable
 * when the API is unreachable; reads fall back to the cache, and
 * a successful refetch re-mirrors the server response back into
 * localStorage so the next load (online or offline) is consistent.
 *
 * On the first authenticated mount, [`useProofsMigration`] pushes
 * any legacy localStorage proofs to the server so a user with a
 * pre-Vault-API browser doesn't lose their history.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as proofsApi from '@/api/proofs';
import type { Proof } from '@/api/proofs';
import { useAuthStore } from '@/stores/authStore';
import { useProofsStore } from '@/stores/proofsStore';

export const proofsKeys = {
  all: ['proofs'] as const,
  list: () => [...proofsKeys.all, 'list'] as const,
  detail: (id: string) => [...proofsKeys.all, 'detail', id] as const,
};

/** List the user's proofs. Falls back to the localStorage cache if
 *  the server is unreachable, and mirrors successful server
 *  responses back into the cache for the next offline load. */
export function useProofs() {
  const user = useAuthStore((s) => s.user);
  const enabled = Boolean(user);
  const mergeServer = useProofsStore((s) => s.mergeServer);
  const localProofs = useProofsStore((s) => s.proofs);

  const query = useQuery<Proof[]>({
    queryKey: proofsKeys.list(),
    queryFn: async () => {
      const proofs = await proofsApi.listProofs();
      // Merge server snapshot into the local cache WITHOUT dropping
      // local-only proofs. The prior `replaceAll` overwrote local
      // with the server's view, which wiped any freshly-signed
      // proof whose POST hadn't landed yet — the actual root cause
      // of "I signed a file but Vault still shows N–1." `mergeServer`
      // keeps local-only ids until the server catches up; at which
      // point the merge becomes a no-op and the row de-duplicates by id.
      mergeServer(proofs);
      // Return the merged shape — including any local-only proofs
      // — so the Vault page renders the in-flight signs immediately
      // without waiting for the next migration cycle.
      const serverIds = new Set(proofs.map((p) => p.id));
      const localOnly = useProofsStore
        .getState()
        .proofs.filter((p) => !serverIds.has(p.id));
      return [...proofs, ...localOnly];
    },
    enabled,
    staleTime: 30_000,
    // When the server is unreachable, the hook falls back to the
    // last cached array so the Vault page still renders something.
    placeholderData: () => localProofs,
  });

  return query;
}

/** Fetch a single proof. */
export function useProof(id: string | undefined) {
  return useQuery<Proof>({
    queryKey: proofsKeys.detail(id ?? ''),
    queryFn: () => proofsApi.getProof(id as string),
    enabled: Boolean(id),
  });
}

/** Save a freshly-signed proof. The legacy `proofsStore.add` is
 *  also invoked so the offline cache + UI components that still
 *  read directly from the Zustand slice update immediately. */
export function useCreateProof() {
  const qc = useQueryClient();
  const addLocal = useProofsStore((s) => s.add);
  return useMutation({
    mutationFn: async (proof: Proof) => {
      await proofsApi.createProof(proof);
      addLocal(proof);
      return proof;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: proofsKeys.list() });
    },
  });
}

/** Delete a proof by id. Optimistically removes from the offline
 *  cache; on server failure we let the next refetch correct the
 *  cache (rare path, no user-visible regression). */
export function useDeleteProof() {
  const qc = useQueryClient();
  const removeLocal = useProofsStore((s) => s.remove);
  return useMutation({
    mutationFn: async (id: string) => {
      await proofsApi.deleteProof(id);
      removeLocal(id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: proofsKeys.list() });
    },
  });
}

/** Migration / resync hook — pushes any localStorage proofs the
 *  server doesn't have yet. Runs on every authenticated mount, not
 *  one-shot: a previous version gated on a `proofs-migrated.<uid>`
 *  flag and stopped re-syncing once it had run, which stranded any
 *  proof whose initial POST failed (the wizard's `addProof` always
 *  writes to localStorage; a server outage during `createProof`
 *  left a row that would never be re-pushed). The diff-and-push
 *  shape below recovers those rows the next time the user lands on
 *  any authenticated page.
 *
 *  Mechanism: fetch the server list, build a Set of server-known
 *  ids, push every local proof whose id is missing. 409 (already
 *  on server, presumably with a different `user_id` from a prior
 *  identity provider) is silently swallowed. Any other error pauses
 *  the resync so a transient outage doesn't spam retries.
 *
 *  Mount this once near the auth boundary (e.g. in `App.tsx` inside
 *  `<ProtectedRoute>`); calling it deeper in the tree is fine since
 *  the diff means a no-op when everything is already in sync. */
export function useProofsMigration(): void {
  const user = useAuthStore((s) => s.user);
  const localProofs = useProofsStore((s) => s.proofs);
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;
    if (localProofs.length === 0) return;
    void (async () => {
      let serverIds: Set<string>;
      try {
        const serverProofs = await proofsApi.listProofs();
        serverIds = new Set(serverProofs.map((p) => p.id));
      } catch (err) {
        console.warn('proofs resync: server list unreachable', err);
        return;
      }
      const missing = localProofs.filter((p) => !serverIds.has(p.id));
      if (missing.length === 0) return;
      let pushed = 0;
      for (const p of missing) {
        try {
          await proofsApi.createProof(p);
          pushed += 1;
        } catch (err) {
          // 409 = already on server (likely under a different
          // `user_id` from a prior identity provider); the
          // server's email-fallback list path will surface it.
          const status = (err as { status?: number } | undefined)?.status;
          if (status !== 409) {
            console.warn('proofs resync paused', err);
            return;
          }
        }
      }
      if (pushed > 0) {
        void qc.invalidateQueries({ queryKey: proofsKeys.list() });
      }
    })();
  }, [user, localProofs, qc]);
}
